import React, { useMemo, useState } from 'react';
import { PageContainer } from 'src/features/Signup/components/organisms/PageContainer/index';
import { AccountDetailStep } from 'src/features/Signup/components/templates/AccountCreationPage/AccountDetailsStep';
// import CreditCardModal from 'src/features/Signup/components/organisms/CreditCardModal';
import { signUp } from 'aws-amplify/auth';
import { FormProvider, useForm } from 'react-hook-form';
import { ErrorNotifBox } from 'src/components/atoms/ErrorNotifBox';
import { useAuthContext } from 'src/contexts/AuthContext';
import { AnalyzingSpinner } from 'src/features/DataUpload/components/organisms/AnalyzingSpinner/index';
import { Box } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

type AccountDetailFormData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  companyAddress: string;
  country: {
    value: string;
    label: string;
  };
  city: string;
  customerCategory: string;
  yearRoundUsage: string;
  seasonalUsage: string;
  projectInfo: string;
};

export const AccountCreationPage: React.FC = () => {
  const [isFormValid, setIsFormValid] = useState(false);
  const [tokenIsMissing, setTokenIsMissing] = useState(false);
  const { signIn } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isJoiningExistingCompany, setIsJoiningExistingCompany] =
    useState(false);
  const navigate = useNavigate();
  // const [isCreditCardModalOpen, setIsCreditCardModalOpen] = useState(false);
  const methods = useForm<AccountDetailFormData>({
    defaultValues: useMemo(
      () => ({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        companyName: '',
        companyAddress: '',
        country: { value: '', label: '' },
        city: '',
        customerCategory: '',
        yearRoundUsage: 'yes',
        seasonalUsage: 'yes',
        projectInfo: '',
      }),
      [],
    ),
    mode: 'onChange',
  });

  const handleNext = async () => {
    const {
      email,
      password,
      firstName,
      lastName,
      companyAddress,
      companyName,
      country,
      city,
      customerCategory,
      yearRoundUsage,
      seasonalUsage,
      projectInfo,
    } = methods.getValues();

    const sp = new URLSearchParams(window.location.search);
    const inviteToken = sp.get('token') ?? undefined;

    if (!inviteToken) {
      setTokenIsMissing(true);
      return;
    }

    // clientMetadata has Record<string, string> type
    try {
      setIsLoading(true);
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            given_name: firstName,
            family_name: lastName,
          },
          ...(inviteToken
            ? {
                clientMetadata: {
                  token: inviteToken,
                  companyName,
                  country: country.label,
                  city,
                  companyAddress,
                  customerCategory,
                  yearRoundUsage,
                  seasonalUsage,
                  projectInfo,
                },
              }
            : {}),
          autoSignIn: { enabled: true },
        },
      });

      // Immediately sign the user in using the newly created credentials
      await signIn(email, password);
      if (isJoiningExistingCompany) {
        navigate('/mmm/overview');
      }
      setIsLoading(false);
    } catch (error: unknown) {
      const normalizedError = {
        type:
          error && typeof error === 'object' && 'name' in error
            ? ((error as { name?: string }).name ?? 'UnknownError')
            : 'UnknownError',
        message:
          error && typeof error === 'object' && 'message' in error
            ? ((error as { message?: string }).message ??
              'Something went wrong')
            : 'Something went wrong',
      };
      if (normalizedError.type === 'InvalidPasswordException') {
        methods.setError('password', {
          type: 'manual',
          message: normalizedError.message,
        });
      } else {
        setErrorMessage(normalizedError.message);
      }
    } finally {
      setIsLoading(false);
    }

    // setIsCreditCardModalOpen(true);
  };

  const handleBack = () => {
    // setCurrentStep((s) => (s > 0 ? s - 1 : s));
  };

  // const handleCreditCardModalClose = () => {
  //   setIsCreditCardModalOpen(false);
  // };

  // const handleCreditCardSubmit = () => {
  //   // Close the modal and set the data integration flag
  //   setIsCreditCardModalOpen(false);
  //   localStorage.setItem('isRegistrationStarted', 'true');
  //   // The app will automatically redirect to DataIntegration page
  // };

  if (errorMessage) {
    return (
      <ErrorNotifBox
        title="Error creating account"
        description={errorMessage}
      />
    );
  }

  if (isLoading) {
    return (
      <Box
        w="100%"
        h="50vh"
        display="flex"
        justifyContent="center"
        alignItems="center">
        <AnalyzingSpinner
          message="Creating an account..."
          boxProps={{ border: 'none', borderRadius: 'none' }}
        />
      </Box>
    );
  }

  return (
    <>
      <PageContainer
        title={'Create the account'}
        subtitle={
          'Start your journey with us — create your account in just a few steps.'
        }
        currentStep={0}
        onNext={handleNext}
        onBack={handleBack}
        isBackDisabled={true}
        isNextDisabled={false}
        isFormValid={isFormValid}>
        {tokenIsMissing && (
          <ErrorNotifBox
            title="Invite token is missing"
            description="Please contact support if you believe this is an error."
          />
        )}
        <FormProvider {...methods}>
          <AccountDetailStep
            onValidationChange={setIsFormValid}
            setIsJoiningExistingCompany={setIsJoiningExistingCompany}
            isJoiningExistingCompany={isJoiningExistingCompany}
          />
        </FormProvider>
      </PageContainer>

      {/* <CreditCardModal
        isOpen={isCreditCardModalOpen}
        onClose={handleCreditCardModalClose}
        onSubmit={handleCreditCardSubmit}
      /> */}
    </>
  );
};
